import { Fragment } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CollapsibleCard } from './CollapsibleCard';

export interface FlatFinancialRow {
  year: number;
  section: string | null;
  subsection: string | null;
  subsubsection?: string | null;
  title: string | null;
  current_value: number | null;
  previous_value: number | null;
}

export function FlatFinancialTable({ 
  data, 
  title = "Financial Data",
  defaultOpen = true
}: { 
  data: FlatFinancialRow[], 
  title?: string,
  defaultOpen?: boolean
}) {
  if (!data || !data.length) {
    return null;
  }

  // 1. Extract unique years
  const years = Array.from(new Set(data.map((r) => r.year))).sort((a, b) => b - a);

  // 2. Group data by "Signature"
  type RowKey = string;
  type RowData = {
    section: string | null;
    subsection: string | null;
    subsubsection?: string | null;
    title: string | null;
    values: Record<number, { current: number | null; previous: number | null }>;
  };

  const grouped = new Map<RowKey, RowData>();

  data.forEach((row) => {
    // specific formatting to ensure uniqueness
    const key = [row.section, row.subsection, row.subsubsection, row.title]
      .map(s => s?.trim() || '')
      .join('||');

    if (!grouped.has(key)) {
      grouped.set(key, {
        section: row.section,
        subsection: row.subsection,
        subsubsection: row.subsubsection,
        title: row.title,
        values: {},
      });
    }

    const entry = grouped.get(key)!;
    entry.values[row.year] = { 
      current: row.current_value,
      previous: row.previous_value
    };
  });

  const rows = Array.from(grouped.values());

  return (
    <CollapsibleCard title={title} className="mt-6" defaultOpen={defaultOpen}>
      <div className="overflow-x-auto">
        <Table className="text-xs">
          <TableHeader className="bg-background z-10 shadow-sm">
            <TableRow className="border-b-2">
              <TableHead className="w-[30%]">Position</TableHead>
              {years.map((year) => (
                <TableHead key={year} className="text-center border-l px-1 h-8" colSpan={2}>
                  {year}
                </TableHead>
              ))}
            </TableRow>
            <TableRow className="border-b">
              <TableHead className="h-6"></TableHead>
              {years.map((year) => (
                <Fragment key={year}>
                  <TableHead className="text-right h-6 px-1 border-l text-[10px] text-muted-foreground uppercase font-semibold w-[80px]">Cur.</TableHead>
                  <TableHead className="text-right h-6 px-1 text-[10px] text-muted-foreground uppercase font-semibold w-[80px]">Prev.</TableHead>
                </Fragment>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {(() => {
              const displayNodes: React.ReactNode[] = [];
              let prevRow: (typeof rows)[0] | null = null;
              const totalCols = 1 + years.length * 2; // Position + (Cur + Prev) * Years

              // Pre-sort rows to ensure Section Sum rows (Title == Section) are at the END of their section block
              const sectionPriority = (name: string | null | undefined) => {
                const normalized = name?.trim().toLowerCase() ?? '';
                // GUV-specific section ordering
                if (normalized === 'rohergebnis') return 1;
                if (normalized === 'betriebsergebnis') return 2;
                if (normalized === 'finanzergebnis') return 3;

                const match = name?.trim().match(/^([A-D])/i);
                if (!match) return 1000;
                const letter = match[1].toUpperCase();
                const orderMap: Record<string, number> = { A: 1, B: 2, C: 3, D: 4 };
                return orderMap[letter] ?? 1000;
              };

              const subsectionPriority = (section: string | null | undefined, name: string | null | undefined) => {
                const sectionNormalized = section?.trim().toLowerCase() ?? '';
                const nameNormalized = name?.trim().toLowerCase() ?? '';

                // GUV-specific subsection ordering inside known sections
                if (sectionNormalized === 'rohergebnis') {
                  if (nameNormalized === 'umsatzerloese') return 1;
                  if (nameNormalized === 'materialaufwand') return 2;
                  return 1000;
                }
                if (sectionNormalized === 'betriebsergebnis') {
                  // keep the typical “other income” then “costs” pattern
                  if (nameNormalized === 'sonstige erträge' || nameNormalized === 'sonstige ertraege') return 1;
                  if (nameNormalized === 'betriebskosten') return 2;
                  return 1000;
                }
                if (sectionNormalized === 'finanzergebnis') {
                  if (nameNormalized === 'sonstige erträge' || nameNormalized === 'sonstige ertraege') return 1;
                  if (nameNormalized === 'zinsen und aehnliche aufwendungen') return 2;
                  return 1000;
                }

                const token = name?.trim().split(/\s+/)[0] || '';
                const orderMap: Record<string, number> = {
                  'I.': 1,
                  'II.': 2,
                  'III.': 3,
                  'IV.': 4,
                  'V.': 5,
                };
                return orderMap[token] ?? 1000;
              };

              const sortedRows = (() => {
                const sectionGroups = new Map<string, typeof rows>();
                rows.forEach((r) => {
                  const sec = r.section || 'UNKNOWN';
                  if (!sectionGroups.has(sec)) sectionGroups.set(sec, []);
                  sectionGroups.get(sec)!.push(r);
                });

                const result: typeof rows = [];
                const orderedSections = Array.from(sectionGroups.keys()).sort((a, b) => {
                  return sectionPriority(a) - sectionPriority(b);
                });

                orderedSections.forEach((sec) => {
                  const group = sectionGroups.get(sec)!;
                  const sectionSumRows: typeof rows = [];
                  const subsectionBuckets = new Map<string, typeof rows>();

                  // Partition into subsection buckets and section sum rows
                  group.forEach((gr) => {
                    const subsectionKey = gr.subsection?.trim() || '__no_subsection__';
                    const isSectionSum =
                      gr.section &&
                      gr.title &&
                      !gr.subsection?.trim() &&
                      isTitleMatch(gr.section, gr.title);

                    if (isSectionSum) {
                      sectionSumRows.push(gr);
                      return;
                    }

                    if (!subsectionBuckets.has(subsectionKey)) {
                      subsectionBuckets.set(subsectionKey, []);
                    }
                    subsectionBuckets.get(subsectionKey)!.push(gr);
                  });

                  // For each subsection bucket, push non-sum rows first then subsection sum rows
                  const orderedSubsections = Array.from(subsectionBuckets.keys()).sort((a, b) => {
                    const pa = subsectionPriority(sec, a);
                    const pb = subsectionPriority(sec, b);
                    if (pa !== pb) return pa - pb;
                    // fallback alphabetical for stability
                    return (a ?? '').localeCompare(b ?? '', 'de-DE', { sensitivity: 'base' });
                  });

                  orderedSubsections.forEach((key) => {
                    const bucket = subsectionBuckets.get(key)!;
                    const subSum: typeof rows = [];
                    const subOthers: typeof rows = [];
                    bucket.forEach((item) => {
                      const isSubSum =
                        item.subsection &&
                        item.title &&
                        isTitleMatch(item.subsection, item.title);
                      if (isSubSum) {
                        subSum.push(item);
                      } else {
                        subOthers.push(item);
                      }
                    });
                    result.push(...subOthers, ...subSum);
                  });

                  // Finally, append section sum rows at the end of the section block
                  result.push(...sectionSumRows);
                });

                return result;
              })();

              // Pre-scan using the SORTED rows to identify sections/subsections/subsubsections that have a sum row
              const sectionsWithSumRow = new Set<string>();
              const subsectionsWithSumRow = new Set<string>();
              const subsubsectionsWithSumRow = new Set<string>();

              const subsectionKey = (row: typeof rows[number]) =>
                `${row.section || ''}||${row.subsection || ''}`.toLowerCase().trim();
              const subsubsectionKey = (row: typeof rows[number]) =>
                `${row.section || ''}||${row.subsection || ''}||${row.subsubsection || ''}`
                  .toLowerCase()
                  .trim();

              sortedRows.forEach((row) => {
                const hasNoSub = !row.subsection?.trim();
                const hasNoSubSub = !row.subsubsection?.trim();

                if (row.section && row.title && hasNoSub && hasNoSubSub && isTitleMatch(row.section, row.title)) {
                  sectionsWithSumRow.add(row.section.trim().toLowerCase());
                }

                if (row.subsection && row.title && isTitleMatch(row.subsection, row.title)) {
                  subsectionsWithSumRow.add(subsectionKey(row));
                }

                if (row.subsubsection && row.title && isTitleMatch(row.subsubsection, row.title)) {
                  subsubsectionsWithSumRow.add(subsubsectionKey(row));
                }
              });

              sortedRows.forEach((row, idx) => {
                const isNewSection = row.section !== prevRow?.section;
                const isNewSubsection = !isNewSection && row.subsection !== prevRow?.subsection;
                const isNewSubsubsection =
                  !isNewSection && !isNewSubsection && row.subsubsection !== prevRow?.subsubsection;
                
                // Match section with title ONLY if subsection is null/empty
                const sectionMatch =
                  isTitleMatch(row.section, row.title) &&
                  !row.subsection?.trim() &&
                  !row.subsubsection?.trim();
                const subsectionMatch = isTitleMatch(row.subsection, row.title);
                const subsubsectionMatch = isTitleMatch(row.subsubsection, row.title);

                // Check if this section has a sum row
                const hasSumRow = row.section ? sectionsWithSumRow.has(row.section.trim().toLowerCase()) : false;

                // 1. Handle Section Level
                
                // Only render a header-only row if this section does NOT have a sum row
                // AND it's a new section
                if (isNewSection && row.section && !hasSumRow) {
                    displayNodes.push(
                      <TableRow key={`sec-${idx}`} className="bg-slate-200 hover:bg-slate-200/90 border-t border-slate-200">
                        <TableCell colSpan={totalCols} className="py-1 px-2 font-bold text-slate-800">
                          {row.section}
                        </TableCell>
                      </TableRow>
                    );
                }

                if (sectionMatch) {
                  // This IS the sum row - render it as a merged section row with values
                  // Use bold font for values as requested
                  displayNodes.push(
                    <TableRow key={`sec-sum-${idx}`} className="bg-slate-200 hover:bg-slate-200/90 border-t border-slate-200">
                      <TableCell className="py-1 px-2 font-bold text-slate-800 truncate max-w-[300px]" title={row.section || ''}>
                        {row.section}
                      </TableCell>
                      {renderValueCells(row, years, true)}
                    </TableRow>
                  );
                  prevRow = row;
                  return; // Done for this row
                }

                // 2. Handle Subsection Level
                if (row.subsection) {
                  const shouldRenderHeader =
                    (isNewSection || isNewSubsection) &&
                    !subsectionsWithSumRow.has(subsectionKey(row));
                  
                  if (subsectionMatch) {
                    // CASE: Title == Subsection -> Render merged Highlighted Subsection Row with Values
                    displayNodes.push(
                      <TableRow key={`sub-sum-${idx}`} className="bg-slate-100 hover:bg-slate-100/90 border-t border-slate-200">
                        <TableCell className="py-1 px-4 font-semibold text-slate-700 truncate max-w-[300px]" title={row.subsection}>
                          {row.subsection}
                        </TableCell>
                         {renderValueCells(row, years)}
                      </TableRow>
                    );
                    prevRow = row;
                    return; // Done for this row
                  } else if (shouldRenderHeader) {
                    // Standard Subsection Header
                    displayNodes.push(
                      <TableRow key={`sub-${idx}`} className="bg-slate-100 hover:bg-slate-100/90 border-t border-slate-200">
                        <TableCell colSpan={totalCols} className="py-1 px-4 font-semibold text-slate-700">
                          {row.subsection}
                        </TableCell>
                      </TableRow>
                    );
                  }
                }

                // 2.5 Handle Sub-subsection Level (third level)
                if (row.subsubsection) {
                  const shouldRenderHeader =
                    (isNewSection || isNewSubsection || isNewSubsubsection) &&
                    !subsubsectionsWithSumRow.has(subsubsectionKey(row));

                  if (subsubsectionMatch) {
                    // CASE: Title == Subsubsection -> render highlighted row with values
                    displayNodes.push(
                      <TableRow key={`subsub-sum-${idx}`} className="bg-slate-50 hover:bg-slate-50/90 border-t border-slate-200">
                        <TableCell className="py-1 px-6 font-semibold text-slate-700 truncate max-w-[300px]" title={row.subsubsection}>
                          {row.subsubsection}
                        </TableCell>
                        {renderValueCells(row, years)}
                      </TableRow>
                    );
                    prevRow = row;
                    return;
                  } else if (shouldRenderHeader) {
                    displayNodes.push(
                      <TableRow key={`subsub-${idx}`} className="bg-slate-50 hover:bg-slate-50/90 border-t border-slate-200">
                        <TableCell colSpan={totalCols} className="py-1 px-6 font-medium text-slate-700">
                          {row.subsubsection}
                        </TableCell>
                      </TableRow>
                    );
                  }
                }

                // 3. Handle Data Level
                // Skip rendering data row if its title matches section/subsection/subsubsection (i.e., it's a sum row already handled)
                const isTitleSameAsParent =
                  (row.title && row.section && isTitleMatch(row.section, row.title)) ||
                  (row.title && row.subsection && isTitleMatch(row.subsection, row.title)) ||
                  (row.title && row.subsubsection && isTitleMatch(row.subsubsection, row.title));

                if (!isTitleSameAsParent) {
                  const depth = row.subsubsection ? 3 : row.subsection ? 2 : row.section ? 1 : 0;
                  const indentClass =
                    depth >= 3 ? 'pl-8' : depth === 2 ? 'pl-6' : depth === 1 ? 'pl-4' : 'pl-2';

                  displayNodes.push(
                    <TableRow 
                      key={`row-${idx}`} 
                      className="hover:bg-muted/50 border-t border-slate-200"
                    >
                      <TableCell className="py-1 px-2 align-middle truncate max-w-[300px]">
                        <div className={`${indentClass} truncate`} title={row.title || ''}>
                           {row.title || '—'}
                        </div>
                      </TableCell>
                      {renderValueCells(row, years)}
                    </TableRow>
                  );
                }
                
                prevRow = row;
              });

              return displayNodes;
            })()}
          </TableBody>
        </Table>
      </div>
    </CollapsibleCard>
  );
}

function renderValueCells(row: { values: Record<number, { current: number | null; previous: number | null }> }, years: number[], isBold = false) {
  return years.map((year) => {
    const vals = row.values[year];
    const isEmpty = !vals?.current && !vals?.previous;
    return (
      <Fragment key={year}>
        <TableCell className={`text-right font-mono py-1 px-1 border-l align-middle ${isEmpty ? 'opacity-30' : ''} ${isBold ? 'font-bold' : ''}`}>
          {formatValue(vals?.current)}
        </TableCell>
        <TableCell className={`text-right font-mono py-1 px-1 text-muted-foreground align-middle ${isEmpty ? 'opacity-30' : ''} ${isBold ? 'font-bold text-slate-600' : ''}`}>
          {formatValue(vals?.previous)}
        </TableCell>
      </Fragment>
    );
  });
}

function formatValue(val: number | null | undefined) {
  if (val === null || val === undefined) return '-';
  return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);
}

function isTitleMatch(category: string | null | undefined, title: string | null | undefined) {
  if (!category || !title) return false;
  const c = category.trim().toLowerCase();
  const t = title.trim().toLowerCase();
  return c === t || c.includes(t) || t.includes(c);
}
